import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuth } from '../hooks/auth.hook';
import { useCurrentUser } from '@/redux/services/portal/user.api';
import { useLocalAuditLogger } from '@/hooks/useLocalAuditLogger';
import Cookies from 'universal-cookie';
import * as enums from '../enums/Enums';
import { useLoadingState } from '@/contexts/LoadingContext';
import { useDispatch } from 'react-redux';
import { setUserProfile } from '@/redux/reducers/userProfile.reducer';
import * as config from '@/config/env.config';

export const Callback = () => {
	const history = useHistory();
	const location = useLocation();
	const dispatch = useDispatch();
	const { isAuthenticated, user, isLoading: auth0IsLoading, error: auth0Error, getAccessTokenSilently } = useAuth0();
	const auth = useAuth();
	const logger = useLocalAuditLogger();
	const db = new Cookies();
	
	const { data: userData, isLoading: userIsLoading, error: userError } = useCurrentUser();

	// Check for email verification error in URL parameters FIRST (before any other processing)
	// This matches the old behavior where error check happened before session processing
	// Universal Login uses query parameters (not hash fragments like old Classic Login)
	useEffect(() => {
		const urlParams = new URLSearchParams(location.search);
		const error = urlParams.get('error');
		const errorDescription = urlParams.get('error_description');
		
		if (error && errorDescription) {
			const decodedError = decodeURIComponent(errorDescription);
			// Check if this is an email verification error (same check as old version)
			if (decodedError.includes("email we have sent you to continue to login.") || 
			    decodedError.includes("email we have sent you")) {
				console.log('📧 Callback: Email verification required (from URL error), redirecting to /verifyemail');
				history.replace("/verifyemail");
				return;
			}
		}
	}, [location.search, history]);

	// Check if this is a new signup with unverified email
	// This handles the case where Auth0 authenticates the user but email isn't verified yet
	useEffect(() => {
		// Only check after Auth0 has finished loading and user is authenticated
		if (auth0IsLoading) {
			return;
		}

		// Check if this is a signup action
		const isSignup = localStorage.getItem('signupAction') === 'create';
		
		// Check if user is authenticated but email is not verified
		if (isAuthenticated && user && isSignup && user.email_verified === false) {
			console.log('📧 Callback: New signup with unverified email, redirecting to /verifyemail', {
				email: user.email,
				email_verified: user.email_verified,
				isSignup
			});
			history.replace("/verifyemail");
			return;
		}
	}, [auth0IsLoading, isAuthenticated, user, history]);

	// Debug: Log token availability
	useEffect(() => {
		if (isAuthenticated && !auth0IsLoading) {
			const checkToken = async () => {
				const { getAuth0Token } = await import('@/utils/auth0Token');
				const token = await getAuth0Token();
				console.log('🔑 Callback: Token check:', {
					hasToken: !!token,
					tokenLength: token ? token.length : 0,
					tokenPreview: token ? token.substring(0, 50) + '...' : 'null',
					cacheKeys: Object.keys(localStorage).filter(k => k.includes('@@auth0spajs@@'))
				});
			};
			checkToken();
		}
	}, [isAuthenticated, auth0IsLoading]);

	const logUserLogin = () => {
		if (localStorage.getItem('signupAction') === 'create') {
			logger.logNewUser();
		} else if (localStorage.getItem('lastAuthAction') === 'login') {
			logger.logUserAction(enums.AUDITLOG_USERACTION_TYPES.LOGIN);
		}
		localStorage.removeItem('lastAuthAction');
		localStorage.removeItem('signupAction');
	};

	// Extract user info from Auth0 user object and set in Redux when authentication completes
	useEffect(() => {
		if (!auth0IsLoading && isAuthenticated && user) {
			// Extract user info from Auth0 user object and set in Redux (like old version did)
			try {
				const username = user.nickname || 
				                user.name?.split('@')[0] || 
				                user.email?.split('@')[0] || 
				                null;
				const email = user.email || null;
				const avatar = user.picture || null;
				const signupSource = user['https://portal.metabolon.com/user_metadata']?.signupSource || null;

				// Set user profile in Redux immediately after authentication
				dispatch(setUserProfile({
					username,
					email,
					avatar,
					signupSource
				}));
			} catch (error) {
				console.error('Error setting user profile from Auth0 in Callback:', error);
			}
		}
	}, [auth0IsLoading, isAuthenticated, user, dispatch]);

	// Handle redirect after user data is loaded
	useEffect(() => {
		if (auth0IsLoading) {
			return;
		}

		if (!isAuthenticated) {
			if (auth0Error) {
				console.error('❌ Callback: Auth0 authentication failed', {
					error: auth0Error.message || 'Unknown error',
					errorDetails: auth0Error
				});
			}
			if (config.ENABLE_SECURITY_ALERT) {
				alert('About to redirect to /securitycheck\n\nState:\n- isAuthenticated: ' + isAuthenticated + '\n- auth0Error: ' + (auth0Error ? JSON.stringify(auth0Error, null, 2) : 'null') + '\n\nCheck console and state before clicking OK');
			}
			history.replace('/securitycheck');
			return;
		}

		if (userIsLoading) {
			console.log('⏳ Callback: Waiting for user data...', { userIsLoading });
			return;
		}

		if (userData) {
			console.log('✅ Callback: User data loaded, redirecting...');
			logUserLogin();
			
			// Check for return URL
			const returnUrl = localStorage.getItem('returnUrl');
			if (returnUrl) {
				// Get token from SDK for cookie
				const setCookieAndRedirect = async () => {
					const token = await getAccessTokenSilently({
						audience: 'https://portal-api'
					});
					if (token) {
						db.set('binf_auth_cookie', token, {
							path: '/',
							sameSite: 'strict',
							domain: window.location.hostname === 'localhost' ? 'localhost' : 'metabolon.com',
							secure: true
						});
					}
					localStorage.removeItem('returnUrl');
					window.location = returnUrl;
				};
				setCookieAndRedirect();
				return;
			}
			
			// Check for state from protected route
			if (history.location.state?.from?.pathname) {
				history.push(history.location.state.from.pathname);
			} else {
				history.push('/home');
			}
		} else if (userError) {
			// Check if this is a new signup with unverified email
			// If so, redirect to verify email instead of security check
			const isSignup = localStorage.getItem('signupAction') === 'create';
			if (isAuthenticated && user && isSignup && user.email_verified === false) {
				console.log('📧 Callback: User data error but email not verified (new signup), redirecting to /verifyemail', {
					email: user.email,
					email_verified: user.email_verified,
					userError: userError.message
				});
				history.replace("/verifyemail");
				return;
			}

			// Check token availability for error logging
			const logError = async () => {
				const { getAuth0Token } = await import('@/utils/auth0Token');
				const token = await getAuth0Token();
				const errorInfo = {
					message: userError.message,
					status: userError.status,
					data: userError.data,
					token: token ? 'Available' : 'Missing',
					url: window.location.href,
					cacheKeys: Object.keys(localStorage).filter(k => k.includes('@@auth0spajs@@'))
				};
				console.error('❌ Callback: User data error:', errorInfo);
				
				if (config.ENABLE_SECURITY_ALERT) {
					alert('About to redirect to /securitycheck\n\nState:\n- isAuthenticated: ' + isAuthenticated + '\n- userError: ' + JSON.stringify(errorInfo, null, 2) + '\n\nCheck console and state before clicking OK');
				}
				history.replace('/securitycheck');
			};
			logError();
			return;
		} else {
			console.log('⏳ Callback: No user data and no error yet, still waiting...', {
				userIsLoading,
				hasUserData: !!userData,
				hasUserError: !!userError
			});
		}
	}, [auth0IsLoading, isAuthenticated, userData, userIsLoading, userError, history, logger, db, auth0Error]);

	// Show loading screen while processing callback
	const showLoading = auth0IsLoading || userIsLoading || (!userData && !userError);
	
	// Register loading state with global loading context
	useLoadingState('callback', showLoading);
	
	return (
		<div data-testid="callback">
			{/* Loading is handled by GlobalLoading component */}
		</div>
	);
};

export default Callback;
